export const YACHT_MIN_PLAYERS = 2;
export const YACHT_MAX_PLAYERS = 12;
export const YACHT_DICE_COUNT = 5;
export const YACHT_MAX_ROLLS = 3;
export const YACHT_UPPER_BONUS_THRESHOLD = 63;
export const YACHT_UPPER_BONUS_POINTS = 35;

export const YACHT_CATEGORIES = [
  "ONES",
  "TWOS",
  "THREES",
  "FOURS",
  "FIVES",
  "SIXES",
  "CHOICE",
  "FOUR_OF_A_KIND",
  "FULL_HOUSE",
  "SMALL_STRAIGHT",
  "LARGE_STRAIGHT",
  "YACHT",
] as const;

export const YACHT_UPPER_CATEGORIES = [
  "ONES",
  "TWOS",
  "THREES",
  "FOURS",
  "FIVES",
  "SIXES",
] as const;

export const YACHT_LOWER_CATEGORIES = [
  "CHOICE",
  "FOUR_OF_A_KIND",
  "FULL_HOUSE",
  "SMALL_STRAIGHT",
  "LARGE_STRAIGHT",
  "YACHT",
] as const;

export type YachtCategory = (typeof YACHT_CATEGORIES)[number];
export type YachtUpperCategory = (typeof YACHT_UPPER_CATEGORIES)[number];
export type YachtLowerCategory = (typeof YACHT_LOWER_CATEGORIES)[number];

export type YachtDieValue = 1 | 2 | 3 | 4 | 5 | 6;
export type YachtDie = YachtDieValue | null;
export type YachtDice = readonly [
  YachtDie,
  YachtDie,
  YachtDie,
  YachtDie,
  YachtDie,
];
export type YachtScoringDice = readonly [
  YachtDieValue,
  YachtDieValue,
  YachtDieValue,
  YachtDieValue,
  YachtDieValue,
];
export type YachtHeldDice = readonly [
  boolean,
  boolean,
  boolean,
  boolean,
  boolean,
];
export type YachtRollCount = 0 | 1 | 2 | 3;

export type YachtPhase = "ROLLING" | "GAME_OVER";
export type YachtTurnMode = "SEQUENTIAL" | "SIMULTANEOUS";
export type YachtTurnModeConfig = "auto" | YachtTurnMode;
export type YachtTurnSlotStatus = "OPEN" | "LOCKED";

export interface ValidationResult {
  readonly valid: boolean;
  readonly error?: string;
  readonly details?: Record<string, unknown>;
}

export interface YachtPlayerConfig {
  readonly playerId: string;
  readonly displayName?: string;
}

export interface InitGameOptions {
  readonly gameId?: string;
  readonly playerIds?: readonly string[];
  readonly players?: readonly YachtPlayerConfig[];
  readonly seed: string;
  readonly turnMode?: YachtTurnModeConfig;
}

export interface YachtPlayer {
  readonly playerId: string;
  readonly displayName: string;
}

export interface YachtTurnRngState {
  readonly algorithm: "mulberry32";
  readonly streamId: string;
  readonly state: number;
}

export interface YachtTurnState {
  readonly turnNumber: number;
  readonly sheetRound: number;
  readonly playerId: string;
  readonly dice: YachtDice;
  readonly held: YachtHeldDice;
  readonly rollCount: YachtRollCount;
  readonly status: YachtTurnSlotStatus;
  readonly rngState: YachtTurnRngState;
}

export interface YachtViewerTurnState {
  readonly turnNumber: number;
  readonly sheetRound: number;
  readonly playerId: string;
  readonly dice: YachtDice;
  readonly held: YachtHeldDice;
  readonly rollCount: YachtRollCount;
  readonly status: YachtTurnSlotStatus;
}

export interface YachtScoreEntry {
  readonly category: YachtCategory;
  readonly score: number;
  readonly dice: YachtScoringDice;
  readonly turnNumber: number;
  readonly sheetRound: number;
  readonly playerId: string;
}

export interface YachtPlayerScoreSheet {
  readonly playerId: string;
  readonly entries: Readonly<Partial<Record<YachtCategory, YachtScoreEntry>>>;
  readonly upperSubtotal: number;
  readonly upperBonus: number;
  readonly lowerSubtotal: number;
  readonly total: number;
}

export interface YachtScoreResult {
  readonly playerId: string;
  readonly category: YachtCategory;
  readonly score: number;
  readonly dice: YachtScoringDice;
  readonly sheetRound: number;
  readonly turnNumber: number;
}

export interface YachtRanking {
  readonly rank: number;
  readonly playerId: string;
  readonly total: number;
}

export interface YachtGameState {
  readonly gameId: string;
  readonly phase: YachtPhase;
  readonly turnMode: YachtTurnMode;
  readonly gameSeed: string;
  readonly sheetRound: number;
  readonly turnNumber: number;
  readonly players: readonly YachtPlayer[];
  readonly seatingOrder: readonly string[];
  readonly turnSlotsByPlayerId: Readonly<Record<string, YachtTurnState>>;
  readonly scoreSheetsByPlayerId: Readonly<
    Record<string, YachtPlayerScoreSheet>
  >;
  readonly lastScoreResult?: YachtScoreResult;
  readonly rankings?: readonly YachtRanking[];
}

export interface YachtLegalActions {
  readonly canRoll: boolean;
  readonly holdableDice: readonly number[];
  readonly scoreableCategories: readonly YachtScorePreview[];
}

export interface YachtScorePreview {
  readonly category: YachtCategory;
  readonly score: number;
  readonly recommended: boolean;
}

export interface YachtViewerState {
  readonly gameId: string;
  readonly phase: YachtPhase;
  readonly turnMode: YachtTurnMode;
  readonly sheetRound: number;
  readonly turnNumber: number;
  readonly players: readonly YachtPlayer[];
  readonly seatingOrder: readonly string[];
  readonly turnSlotsByPlayerId: Readonly<Record<string, YachtViewerTurnState>>;
  readonly scoreSheetsByPlayerId: Readonly<
    Record<string, YachtPlayerScoreSheet>
  >;
  readonly legalActions: YachtLegalActions;
  readonly viewerPlayerId: string | null;
  readonly lastScoreResult?: YachtScoreResult;
  readonly rankings?: readonly YachtRanking[];
}

export interface YachtRollDiceAction {
  readonly type: "ROLL_DICE";
  readonly playerId: string;
  readonly sheetRound: number;
  readonly turnNumber: number;
}

export interface YachtToggleHoldAction {
  readonly type: "TOGGLE_HOLD";
  readonly playerId: string;
  readonly sheetRound: number;
  readonly turnNumber: number;
  readonly dieIndex: number;
}

export interface YachtScoreCategoryAction {
  readonly type: "SCORE_CATEGORY";
  readonly playerId: string;
  readonly sheetRound: number;
  readonly turnNumber: number;
  readonly category: YachtCategory;
}

export type YachtAction =
  | YachtRollDiceAction
  | YachtToggleHoldAction
  | YachtScoreCategoryAction;

export type YachtReducerResult =
  | { readonly valid: true; readonly state: YachtGameState }
  | {
      readonly valid: false;
      readonly state: YachtGameState;
      readonly error: string;
      readonly details?: Record<string, unknown>;
    };
